import { Button, Grid, Theme, Typography, WithStyles, withStyles, withTheme } from '@material-ui/core';
import { IActivity } from '@pbl/pbl-react-core/lib/models/activity/types';
import { IPreferenceCategory, IPreferencesState } from '@pbl/pbl-react-core/lib/models/preferences';
import AppSpinner from '@pbl/pbl-react-web-components/lib/app-spinner/AppSpinner';
import { EarnPointsModal } from '@pbl/pbl-react-web-components/lib/package';
import styles from 'assets/jss/modules/settings/preferences/PreferencesScreenStyle';
import _ from 'lodash';
import React from 'react';
import { connect } from 'react-redux';
import { IRootState } from 'redux/reducers';
import { getActivityWithFreqCap, getPromotionsEmailActivity } from 'redux/reducers/activity/actions';
import {
  fetchMyPreferences,
  fetchOptions,
  loadMyPreferences,
  savePreferences,
  toggleAllPreference,
  togglePreference,
  updateSubCategory
} from 'redux/reducers/preferences/actions';
import Preference from './components/Preference';

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

interface IPreferencesScreenProps extends StateProps, DispatchProps, WithStyles<typeof styles> {
  theme: Theme;
}

interface IPreferencesScreenState {
  activityCompleted: boolean;
  promotionsEmailActivity?: IActivity;
  showPopup: boolean;
}

const ALL = '__ALL__';

class PreferencesScreen extends React.Component<IPreferencesScreenProps, IPreferencesScreenState> {
  constructor(props: IPreferencesScreenProps) {
    super(props);
    this.state = { activityCompleted: false, promotionsEmailActivity: undefined, showPopup: false };
  }

  public async componentDidMount() {
    document.title = 'Preferences';
    await this.getData();
  }

  public render() {
    const { classes, preferences, loading } = this.props;
    const { promotionsEmailActivity, showPopup } = this.state;

    return (
      <Grid container={true} spacing={3} direction="column">
        {loading && <AppSpinner label={'Loading...'} />}

        {showPopup && !!promotionsEmailActivity ? (
          <EarnPointsModal
            pointEarned={promotionsEmailActivity.amount}
            isVisible={showPopup}
            onClose={this.onDismissEarnPointsModal}
          />
        ) : null}

        <Grid container={true} direction="row" alignItems="center" className={classes.header}>
          <Typography variant="body2" className={classes.headerText}>
            Choose when you want to receive emails from us.
          </Typography>
        </Grid>
        <div>
          <Preference
            item={{
              key: ALL,
              booleanValue: preferences.all || false,
              label: 'Send me all email communications',
              subCategories: preferences.categories,
              options: preferences.options
            }}
            onToggle={this.onTogglePreference}
            handlePreferenceDropdownChange={this.handlePreferenceDropdownChange}
          />
        </div>
        <Grid item={true}>
          <Button size="large" variant="contained" color="primary" className={classes.button} onClick={this.onSave} aria-label="Save">
            Save
          </Button>
        </Grid>
      </Grid>
    );
  }

  private getData = async (isPolling = false) => {
    if (!isPolling) { // Only update this when not polling
      await this.props.fetchMyPreferences();
      await this.props.loadMyPreferences();
      await this.props.fetchOptions();
    }

    const activityData = await getPromotionsEmailActivity(this.props.loggedIn);

    if (activityData) {
      this.setState(prevState => ({
        ...prevState,
        promotionsEmailActivity: activityData,
        activityCompleted: activityData?.frequencyCap.limitReached
      }));
    }
  };

  private onDismissEarnPointsModal = (): void => {
    if (this.state.promotionsEmailActivity && this.state.promotionsEmailActivity.frequencyCap.limitReached) {
      this.setState(prevState => ({ ...prevState, showPopup: false }));
    }
  };

  private onTogglePreference = (name: string) => {
    if (name === ALL) {
      this.props.toggleAllPreference();
    } else {
      this.props.togglePreference(name);
    }
  };

  public onSave = async () => {
    await this.props.savePreferences();
    if (this.isNewsEmailPreferenceToggledOn() && !this.state.activityCompleted) {
      await this.determineShowPopup();
    }
  };

  /**
   * Compares previous state of the app with current fetch to check if the earn points popup can be shown
   * This function sets the showPopup & should be called on save only
   * to make sure we don't update & fetch uneeded data
   * Logic is kind of similar to the from profile-screen
   */
  private determineShowPopup = async () => {
    const activityData = await getPromotionsEmailActivity(this.props.loggedIn);
    if (activityData) {
      // if a completion is triggered the now & prev will be different, triggering the popup to show
      const isNowComplete = activityData.frequencyCap.limitReached;
      this.setState(prevState => ({
        ...prevState,
        promotionsEmailActivity: activityData,
        activityCompleted: isNowComplete,
        showPopup: isNowComplete !== prevState.activityCompleted
      }));

    }
  }

  public isNewsEmailPreferenceToggledOn = (): boolean =>
    this.props.preferences && !!this.props.preferences.categories.find(p => p.key === 'PROMOTIONS_EMAIL' && p.booleanValue);

  public handlePreferenceDropdownChange = (subCategory: IPreferenceCategory, value: number) => {
    const { categories } = this.props.preferences;
    if (subCategory.options) {
      const option = subCategory.options.find(opt => opt.id === value);
      // @ts-ignore
      subCategory.value = option;
      const category = categories.find((c: any) => {
        if (!_.isEmpty(c.subCategories)) {
          return _.map(c.subCategories, sub => {
            // @ts-ignore
            if (sub.MessageCategoryId === option.messageCategoryId) {
              sub.value = { ...sub.value, ...option };
              return c;
            }
          });
        }
        return undefined;
      });
      this.props.updateSubCategory(category);
    }
  };
}

const mapStateToProps = ({ authentication: { account, accessToken }, preferences: { tempState, fetching } }: IRootState) => {
  const loggedIn: boolean = !!accessToken && accessToken.length > 0 && !!account && !!account.email;

  return {
    loggedIn,
    preferences: tempState || ({} as IPreferencesState),
    loading: fetching
  };
};

const mapDispatchToProps = {
  toggleAllPreference,
  togglePreference,
  fetchMyPreferences,
  loadMyPreferences,
  fetchOptions,
  getActivityWithFreqCap,
  savePreferences,
  updateSubCategory
};

// @ts-ignore
export default connect(mapStateToProps, mapDispatchToProps)(withTheme(withStyles(styles)(PreferencesScreen)));
